import { onConsentChange, type ConsentCategory } from "../consent/consent";

import { attributionFrom, categoryAttribution, type CheckoutAttribution } from "./attribution";
import { cookieStore, readCookie, type TrackingState, type TrackingStore } from "./cookies";

export type { CheckoutAttribution } from "./attribution";
export const FIRST_AD_STEPS = ["entry", "link_pasted", "template_picked", "signup", "product_found", "actor", "script",
  "keyframes", "get_video", "free_ad_made", "plans", "checkout_started", "purchase", "second_ad_made"] as const;
export type FirstAdStep = typeof FIRST_AD_STEPS[number];
export interface FirstAdContext { template?: string }
export interface FunnelEvent {
  event: "funnel_step";
  distinct_id: string;
  properties: CheckoutAttribution & { funnel: "free_ad_first"; page: "1"; step: FirstAdStep; step_index: number; event_id: string; template?: string };
}
export interface MetaEvent {
  event_name: "PageView" | "ViewContent" | "GoToApp" | "Contact" | "CompleteRegistration" | "Purchase";
  event_id: string;
  custom_data: { plan_set?: "openart"; page?: "1"; value?: number; currency?: string };
}
/** Receivers must deduplicate event_id too, including a delivery acknowledged after a timeout. */
export interface TrackingSinks {
  posthog?: (event: FunnelEvent) => Promise<void>;
  meta?: (event: MetaEvent) => Promise<void>;
}
export type Delivery = "disabled" | "blocked" | "duplicate" | "sent" | "failed";
const safeReference = /^[A-Za-z0-9:_-]{1,120}$/;

/** No real transport is installed. Tests inject sinks; approved SDK configuration is a later change. */
export function createFirstAdTracking(sinks: TrackingSinks = {}, store: TrackingStore = cookieStore) {
  const state = (category: ConsentCategory): TrackingState => store.read(category) ?? {
    v: 1, id: crypto.randomUUID(), sent: [], pages: [], auth: [],
  };

  function captureAttribution(search = window.location.search): CheckoutAttribution {
    const query = new URLSearchParams(search);
    const incoming = attributionFrom({ ...Object.fromEntries(query), fbp: readCookie("_fbp"), fbc: readCookie("_fbc") });
    if (incoming.fbclid && !incoming.fbc) incoming.fbc = `fb.1.${Date.now()}.${incoming.fbclid}`;
    for (const category of ["analytics", "advertising"] as const) {
      if (!store.allowed(category)) continue;
      const saved = store.readAttribution(category);
      // First touch wins; navigation without attribution never empties the cookie.
      store.writeAttribution(category, { ...categoryAttribution(incoming, category), ...saved });
    }
    return checkoutAttribution();
  }

  function checkoutAttribution(): CheckoutAttribution {
    const result: CheckoutAttribution = {};
    for (const category of ["analytics", "advertising"] as const) {
      if (store.allowed(category)) Object.assign(result, store.readAttribution(category));
    }
    return result;
  }

  async function deliverUnlocked(category: ConsentCategory, receipt: string, send: ((id: string, saved: TrackingState) => Promise<void>) | undefined): Promise<Delivery> {
    if (!store.allowed(category)) return "blocked";
    if (!send) return "disabled";
    const saved = state(category);
    const pageView = receipt.startsWith("page:");
    const sharedAuth = receipt.startsWith("auth:");
    const key = sharedAuth ? receipt.slice(5) : receipt;
    const receipts = pageView ? saved.pages : [...saved.sent, ...saved.auth];
    if (receipts.includes(key)) return "duplicate";
    const reserved = pageView ? { ...saved, pages: [...saved.pages, key].slice(-8) }
      : sharedAuth ? { ...saved, auth: [...saved.auth, key].slice(-16) }
      : { ...saved, sent: [...saved.sent, key] };
    if ((!pageView && !sharedAuth && saved.sent.length >= 64) || !store.write(category, reserved)) return "blocked";
    try {
      await send(`${saved.id}:${receipt}`, saved);
      return "sent";
    } catch {
      // Preserve receipts another synchronous call reserved while this sink was in flight.
      const current = store.read(category);
      if (current?.id === saved.id) store.write(category, pageView
        ? { ...current, pages: current.pages.filter((value) => value !== key) }
        : sharedAuth ? { ...current, auth: current.auth.filter((value) => value !== key) }
        : { ...current, sent: current.sent.filter((value) => value !== key) });
      return "failed";
    }
  }

  function deliver(category: ConsentCategory, receipt: string, send: ((id: string, saved: TrackingState) => Promise<void>) | undefined): Promise<Delivery> {
    // Web Locks serialize cookie read/modify/write across same-origin tabs. Receivers still
    // deduplicate IDs for crashes and different subdomains, which do not share a lock manager.
    return navigator.locks ? navigator.locks.request(`createdai-first-ad-${category}`, () => deliverUnlocked(category, receipt, send))
      : deliverUnlocked(category, receipt, send);
  }

  function meta(event_name: MetaEvent["event_name"], receipt: string, confirmedId?: string, amount?: { value: number; currency: string }, tags: MetaEvent["custom_data"] = { plan_set: "openart", page: "1" }) {
    return deliver("advertising", receipt, sinks.meta ? (id) => sinks.meta!({
      event_name, event_id: confirmedId ?? id, custom_data: { ...tags, ...amount },
    }) : undefined);
  }

  async function recordFirstAdStep(step: FirstAdStep, context: FirstAdContext = {}) {
    const template = context.template && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(context.template) && context.template.length <= 64 ? context.template : undefined;
    const analytics = deliver("analytics", step, sinks.posthog ? (id, saved) => sinks.posthog!({
      event: "funnel_step", distinct_id: saved.id, properties: {
        ...categoryAttribution(checkoutAttribution(), "analytics"), funnel: "free_ad_first", page: "1", step,
        step_index: FIRST_AD_STEPS.indexOf(step) + 1, event_id: id, ...(template ? { template } : {}),
      },
    }) : undefined);
    const advertising = step === "plans" ? meta("ViewContent", "plans")
      : step === "template_picked" || step === "checkout_started" ? meta("GoToApp", step) : Promise.resolve<Delivery>("disabled");
    return { analytics: await analytics, advertising: await advertising };
  }

  /** IDs come from auth's logical attempt/server response, shared with its CAPI event. */
  function authMeta(event: "Contact" | "CompleteRegistration", eventId: string, tags: MetaEvent["custom_data"], receipt = eventId) {
    const prefix = event === "Contact" ? "contact:" : "complete_registration:";
    return eventId.startsWith(prefix) && safeReference.test(eventId) ? meta(event, receipt, eventId, undefined, tags) : Promise.resolve<Delivery>("blocked");
  }

  function recordFirstAdMeta(event: "Contact" | "CompleteRegistration", eventId: string) {
    return authMeta(event, eventId, { plan_set: "openart", page: "1" });
  }

  /** Shared auth never assigns a first-ad flow; its caller supplies authoritative auth IDs. */
  function recordAuthConversion(event: "Contact" | "CompleteRegistration", eventId: string) {
    return authMeta(event, eventId, {}, `auth:${eventId}`);
  }

  /** Call for each router navigation, with an opaque ID; never pass a credential-bearing URL. */
  function recordFirstAdPageView(navigationId: string) {
    return safeReference.test(navigationId) ? meta("PageView", `page:${navigationId}`) : Promise.resolve<Delivery>("blocked");
  }

  /** Only an authoritative server response/webhook supplies these facts, never search params. */
  function recordConfirmedPurchase(confirmation: { event_id: string; amount_minor: number; currency: string }) {
    if (!/^purchase:[A-Za-z0-9_-]{1,100}$/.test(confirmation.event_id) || !Number.isSafeInteger(confirmation.amount_minor)
      || confirmation.amount_minor < 0 || !/^[A-Z]{3}$/.test(confirmation.currency)) return Promise.resolve<Delivery>("blocked");
    return meta("Purchase", confirmation.event_id, confirmation.event_id, { value: confirmation.amount_minor / 100, currency: confirmation.currency });
  }

  /** Mount once in app composition. A consent answer captures only the current page's query. */
  function observeFirstAdAttribution(): () => void {
    captureAttribution();
    return onConsentChange(() => { captureAttribution(); });
  }

  return { recordAuthConversion, observeFirstAdAttribution, captureAttribution, checkoutAttribution, recordFirstAdStep, recordFirstAdMeta, recordFirstAdPageView, recordConfirmedPurchase };
}

export const { recordAuthConversion, observeFirstAdAttribution, captureAttribution, checkoutAttribution, recordFirstAdStep, recordFirstAdMeta, recordFirstAdPageView, recordConfirmedPurchase } = createFirstAdTracking();
